/**
 * Billing settings page — /w/:slug/app/settings/billing.
 *
 * See docs/users-onboarding-billing-plan.md §4.5.
 *
 * Sections:
 *   1. Current plan card — plan name, status, next-renewal / trial-end.
 *   2. Archive banner (only when in_grace) — day counter + Restore CTA.
 *   3. Pending-cancel notice (only when cancel_at_period_end + not archived)
 *      — reversal button.
 *   4. Upgrade CTA (Free workspaces) — trial start button.
 *   5. Cancel plan button (Owners only, paid tiers only) — with a
 *      one-time "save the account" offer per plan §4.5.
 *
 * Owner-vs-non-owner UX:
 *   - Non-owners see plan/status but the Cancel / Restore / Trial
 *     buttons are hidden. Server enforces requireRole('owner') on the
 *     mutating endpoints, so a client bypass just gets a 403.
 */

import { useCallback, useEffect, useState } from 'react';
import { SettingsCard } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { getBillingSummary, startBillingTrial, cancelBilling, subscribeByInvoice as subscribeByInvoiceApi, restoreBilling } from '../lib/api';

interface BillingSummary {
  plan_id: string;
  plan_name: string;
  tier: 'free' | 'growth' | 'enterprise';
  status:
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired';
  cancel_at_period_end: boolean;
  trial_ends_at: string | null;
  current_period_end: string | null;
  archived_at: string | null;
  archive_days_remaining: number | null;
  in_grace: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: BillingSummary }
  | { kind: 'error'; message: string };

export function Billing() {
  const { workspace, role } = useWorkspace();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showSaveOffer, setShowSaveOffer] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  // Success payload from POST /subscribe-by-invoice — keeps the hosted
  // invoice URL visible after the flow so AP can copy it directly.
  const [invoiceResult, setInvoiceResult] = useState<{
    hosted_invoice_url: string | null;
    invoice_pdf_url: string | null;
    /** When Stripe expects payment — the "invoice due" date for the AP team. */
    invoice_due: string | null;
  } | null>(null);
  const isOwner = role === 'owner';

  const reload = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const body = await getBillingSummary(workspace.slug);
      setState({ kind: 'ready', summary: body as BillingSummary });
    } catch (e) {
      setState({
        kind: 'error',
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [workspace.slug]);

  useEffect(() => {
    reload();
  }, [reload]);

  const startTrial = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const body = await startBillingTrial(workspace.slug, 'growth-annual');
      setNotice(
        `Trial active until ${body.trial_ends_at ? new Date(body.trial_ends_at).toLocaleDateString() : '14 days from now'}.`
      );
      await reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Couldn't start trial.");
    } finally {
      setBusy(false);
    }
  }, [workspace.slug, reload]);

  const cancel = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      await cancelBilling(workspace.slug);
      setNotice(
        'Cancellation scheduled. Your workspace stays on this plan until the end of the current billing period, then archives for 90 days.'
      );
      setShowSaveOffer(false);
      await reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Couldn't cancel.");
    } finally {
      setBusy(false);
    }
  }, [workspace.slug, reload]);

  const subscribeByInvoice = useCallback(
    async (form: {
      customer_name: string;
      billing_email: string;
      po_reference?: string;
      plan_id: string;
    }) => {
      setBusy(true);
      setNotice(null);
      setInvoiceResult(null);
      try {
        const body = await subscribeByInvoiceApi(workspace.slug, form);
        // "Invoice due" = trial_ends_at when a trial was used (the
        // real invoice fires at trial end); otherwise current_period_end.
        setInvoiceResult({
          hosted_invoice_url: body.hosted_invoice_url ?? null,
          invoice_pdf_url: body.invoice_pdf_url ?? null,
          invoice_due: body.trial_ends_at ?? body.current_period_end ?? null,
        });
        setShowInvoiceForm(false);
        setNotice(
          `Invoice sent to ${form.billing_email}. Your workspace has immediate access; AP has 30 days to pay.`
        );
        await reload();
      } catch (e) {
        setNotice(e instanceof Error ? e.message : "Couldn't create invoice.");
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  const restore = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      await restoreBilling(workspace.slug);
      setNotice('Cancellation reversed — your plan continues at renewal.');
      await reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Couldn't restore.");
    } finally {
      setBusy(false);
    }
  }, [workspace.slug, reload]);

  if (state.kind === 'loading') {
    return (
      <div style={shell} aria-busy="true">
        Loading billing…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div style={shell}>
        <h1>Billing</h1>
        <p style={{ color: 'crimson' }}>{state.message}</p>
      </div>
    );
  }

  const s = state.summary;
  return (
    <div style={shell}>
      <h1 style={{ marginTop: 0 }}>Billing</h1>

      {notice ? (
        <div
          role="status"
          style={{
            padding: '0.75rem 1rem',
            border: '1px solid #ccc',
            background: 'var(--surface-elevated)',
            borderRadius: '0.35rem',
          }}
        >
          {notice}
        </div>
      ) : null}

      <PlanCard summary={s} />

      {s.in_grace && s.archive_days_remaining !== null ? (
        <ArchiveBanner
          daysRemaining={s.archive_days_remaining}
          onRestart={isOwner ? startTrial : undefined}
        />
      ) : null}

      {s.cancel_at_period_end && !s.in_grace ? (
        <PendingCancelNotice
          currentPeriodEnd={s.current_period_end}
          onRestore={isOwner ? restore : undefined}
          busy={busy}
        />
      ) : null}

      {s.tier === 'free' && !s.in_grace ? (
        <>
          <UpgradeCta
            onStartTrial={isOwner ? startTrial : undefined}
            onOpenInvoiceForm={
              isOwner ? () => setShowInvoiceForm(true) : undefined
            }
            busy={busy}
          />
          {showInvoiceForm ? (
            <InvoiceForm
              onCancel={() => setShowInvoiceForm(false)}
              onSubmit={subscribeByInvoice}
              busy={busy}
            />
          ) : null}
          {invoiceResult && invoiceResult.hosted_invoice_url ? (
            <InvoiceResultCard result={invoiceResult} />
          ) : null}
        </>
      ) : null}

      {isOwner && s.tier !== 'free' && !s.cancel_at_period_end && !s.in_grace ? (
        <CancelBlock
          onCancel={cancel}
          onOpenSaveOffer={() => setShowSaveOffer(true)}
          showSaveOffer={showSaveOffer}
          onDismissOffer={() => setShowSaveOffer(false)}
          busy={busy}
        />
      ) : null}
    </div>
  );
}

// ---- Plan card --------------------------------------------------------

function PlanCard({ summary: s }: { summary: BillingSummary }) {
  const statusLabel = s.status.replace('_', ' ');
  const nextDate = formatDate(s.current_period_end);
  const trialDate = formatDate(s.trial_ends_at);
  // PR-C0-1c (docs/cluster-0-chrome-sweep-plan.md): rewired from an
  // inline `section style={{ border: '1px solid #ddd' ... }}` to the
  // extracted `<SettingsCard />` DS component. Card chrome routes
  // through tokens (`var(--border)`, `var(--surface)`, radius token)
  // and looks the same on legacy + Direction B cascades.
  return (
    <SettingsCard
      title={s.plan_name}
      status={statusLabel}
      statusTone={billingStatusTone(s.status)}
    >
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '0.35rem 1rem',
          margin: 0,
          fontSize: '0.9rem',
          color: 'var(--text-muted)',
        }}
      >
        <dt>Tier</dt>
        <dd style={{ margin: 0 }}>{s.tier}</dd>
        {trialDate ? (
          <>
            <dt>Trial ends</dt>
            <dd style={{ margin: 0 }}>{trialDate}</dd>
          </>
        ) : null}
        {nextDate ? (
          <>
            <dt>{s.cancel_at_period_end ? 'Coverage ends' : 'Next renewal'}</dt>
            <dd style={{ margin: 0 }}>{nextDate}</dd>
          </>
        ) : null}
      </dl>
    </SettingsCard>
  );
}

function billingStatusTone(status: string): 'success' | 'warning' | 'critical' | 'muted' {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
      return 'warning';
    case 'canceled':
    case 'incomplete_expired':
      return 'critical';
    default:
      return 'muted';
  }
}

// PR-C0-1c: `statusColor(status)` retired. Consumers now use the
// tone-mapping `billingStatusTone()` above which feeds
// `<SettingsCard statusTone />`. Removing keeps the single source of
// truth for status→color routing inside the SettingsCard component.

// ---- Archive banner ---------------------------------------------------

function ArchiveBanner({
  daysRemaining,
  onRestart,
}: {
  daysRemaining: number;
  onRestart?: () => void;
}) {
  return (
    <section
      aria-label="Archive window"
      role="alert"
      style={{
        border: '2px solid #d68910',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        background: 'var(--surface-elevated)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div>
        <strong style={{ fontSize: '1.05rem' }}>
          {daysRemaining} day{daysRemaining === 1 ? '' : 's'} until your data is purged
        </strong>
        <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted)' }}>
          Your subscription was cancelled and this workspace is in a 90-day
          read-only archive. After the window closes, the register and reports
          are permanently deleted. Any PDFs you already downloaded remain
          yours.
        </p>
      </div>
      {onRestart ? (
        <button type="button" onClick={onRestart} style={btnPrimary}>
          Restart with a 14-day trial
        </button>
      ) : (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Only the workspace Owner can restart the subscription.
        </p>
      )}
    </section>
  );
}

// ---- Pending-cancel notice --------------------------------------------

function PendingCancelNotice({
  currentPeriodEnd,
  onRestore,
  busy,
}: {
  currentPeriodEnd: string | null;
  onRestore?: () => void;
  busy: boolean;
}) {
  const dateStr = formatDate(currentPeriodEnd) ?? 'the end of this period';
  return (
    <section
      aria-label="Cancellation pending"
      role="alert"
      style={{
        border: '1px solid #d68910',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        background: 'var(--surface-elevated)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div>
        <strong>Cancellation scheduled</strong>
        <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted)' }}>
          Your plan continues until {dateStr}, then the workspace enters a
          90-day read-only archive.
        </p>
      </div>
      {onRestore ? (
        <button type="button" onClick={onRestore} disabled={busy} style={btnPrimary}>
          Keep my plan
        </button>
      ) : null}
    </section>
  );
}

// ---- Upgrade CTA (Free) -----------------------------------------------

function UpgradeCta({
  onStartTrial,
  onOpenInvoiceForm,
  busy,
}: {
  onStartTrial?: () => void;
  onOpenInvoiceForm?: () => void;
  busy: boolean;
}) {
  return (
    <section
      aria-label="Upgrade"
      style={{
        border: '2px solid var(--accent)',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        background:
          'linear-gradient(135deg, rgba(34,81,214,0.06), rgba(34,81,214,0.02))',
        textAlign: 'center',
      }}
    >
      <h2 style={{ marginTop: 0 }}>Try Growth free for 14 days</h2>
      <p style={{ color: 'var(--text-muted)' }}>
        Full register with remediation guidance, framework scorecards,
        48-hour alerts, OIDC SSO, API access. No card required.
      </p>
      {onStartTrial ? (
        <>
          <button
            type="button"
            onClick={onStartTrial}
            disabled={busy}
            style={{ ...btnPrimary, padding: '0.85rem 1.5rem' }}
          >
            {busy ? 'Starting…' : 'Start 14-day Growth trial'}
          </button>
          {onOpenInvoiceForm ? (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={onOpenInvoiceForm}
                disabled={busy}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textDecoration: 'underline',
                }}
              >
                Pay by invoice instead (NET 30) — for AP departments
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
          Only the workspace Owner can start a trial.
        </p>
      )}
    </section>
  );
}

// ---- Invoice form (NET 30 flow) ---------------------------------------

function InvoiceForm({
  onCancel,
  onSubmit,
  busy,
}: {
  onCancel: () => void;
  onSubmit: (input: {
    customer_name: string;
    billing_email: string;
    po_reference?: string;
    plan_id: string;
  }) => void;
  busy: boolean;
}) {
  const [customerName, setCustomerName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [poRef, setPoRef] = useState('');
  const [planId, setPlanId] = useState('growth-annual');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      customer_name: customerName.trim(),
      billing_email: billingEmail.trim(),
      po_reference: poRef.trim() || undefined,
      plan_id: planId,
    });
  };

  return (
    <section
      aria-label="Pay by invoice"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: '1.15rem' }}>
        Pay by invoice (NET 30)
      </h2>
      <p style={{ color: 'var(--text-muted)', marginTop: '0.35rem' }}>
        No card required. You get immediate access; we email your AP
        department an invoice they can pay by card, ACH, or wire
        within 30 days. Standard for enterprise procurement.
      </p>
      <form
        onSubmit={submit}
        style={{ display: 'grid', gap: '0.85rem', marginTop: '1rem' }}
      >
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.9rem' }}>
          Plan
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            disabled={busy}
            style={{
              padding: '0.55rem 0.75rem',
              border: '1px solid #ccc',
              borderRadius: '0.35rem',
              fontSize: '0.95rem',
            }}
          >
            <option value="growth-annual">Growth — $34,900 / year</option>
            <option value="growth-2yr">Growth — 2-year, $59,330 (15% off)</option>
            <option value="growth-3yr">Growth — 3-year, $78,525 (25% off)</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.9rem' }}>
          Company legal name (as it should appear on the invoice)
          <input
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
            minLength={2}
            maxLength={200}
            placeholder="Acme Corp, Inc."
            disabled={busy}
            style={{
              padding: '0.55rem 0.75rem',
              border: '1px solid #ccc',
              borderRadius: '0.35rem',
              fontSize: '0.95rem',
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.9rem' }}>
          Billing email (typically accounts-payable@company.com)
          <input
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            required
            placeholder="accounts-payable@acme.com"
            disabled={busy}
            style={{
              padding: '0.55rem 0.75rem',
              border: '1px solid #ccc',
              borderRadius: '0.35rem',
              fontSize: '0.95rem',
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.9rem' }}>
          PO reference / cost center (optional — appears on the invoice)
          <input
            type="text"
            value={poRef}
            onChange={(e) => setPoRef(e.target.value)}
            maxLength={100}
            placeholder="PO-2026-0847"
            disabled={busy}
            style={{
              padding: '0.55rem 0.75rem',
              border: '1px solid #ccc',
              borderRadius: '0.35rem',
              fontSize: '0.95rem',
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button type="button" onClick={onCancel} disabled={busy} style={btnGhost}>
            Back
          </button>
          <button
            type="submit"
            disabled={busy || !customerName.trim() || !billingEmail.trim()}
            style={btnPrimary}
          >
            {busy ? 'Creating invoice…' : 'Send invoice to AP'}
          </button>
        </div>
      </form>
    </section>
  );
}

function InvoiceResultCard({
  result,
}: {
  result: {
    hosted_invoice_url: string | null;
    invoice_pdf_url: string | null;
    invoice_due: string | null;
  };
}) {
  const dueDate = formatDate(result.invoice_due);
  return (
    <section
      aria-label="Invoice created"
      style={{
        border: '2px solid #1e8449',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        background: 'rgba(30,132,73,0.06)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Invoice created ✓</h2>
      <p style={{ color: 'var(--text-muted)' }}>
        Stripe has emailed the invoice. Your workspace has full access
        immediately. Invoice due {dueDate ?? 'in 30 days'}.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {result.hosted_invoice_url ? (
          <a
            href={result.hosted_invoice_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block' }}
          >
            View / pay hosted invoice
          </a>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Hosted URL not yet available — the invoice was queued and Stripe will
            email it shortly.
          </span>
        )}
        {result.invoice_pdf_url ? (
          <a
            href={result.invoice_pdf_url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block' }}
          >
            Download PDF
          </a>
        ) : null}
      </div>
    </section>
  );
}

// ---- Cancel block with save-the-account offer -------------------------

function CancelBlock({
  onCancel,
  onOpenSaveOffer,
  showSaveOffer,
  onDismissOffer,
  busy,
}: {
  onCancel: () => void;
  onOpenSaveOffer: () => void;
  showSaveOffer: boolean;
  onDismissOffer: () => void;
  busy: boolean;
}) {
  if (showSaveOffer) {
    return (
      <section
        aria-label="Save-the-account offer"
        style={{
          border: '1px solid #ddd',
          borderRadius: '0.5rem',
          padding: '1.25rem 1.5rem',
          background: 'var(--surface)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Wait — one thing</h2>
        <p style={{ color: 'var(--text-muted)' }}>
          If it's a fit issue, we'd like to keep you on. Reply to your invoice
          email and ask for the "2 months free" retention credit — one per
          workspace, no questions. Otherwise you can proceed with
          cancellation below; your workspace stays live until the end of the
          current billing period.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" onClick={onDismissOffer} style={btnGhost}>
            Back
          </button>
          <button type="button" onClick={onCancel} disabled={busy} style={btnDanger}>
            {busy ? 'Cancelling…' : 'Yes, cancel my plan'}
          </button>
        </div>
      </section>
    );
  }
  return (
    <section
      aria-label="Cancel plan"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Cancel plan</h2>
      <p style={{ color: 'var(--text-muted)', margin: 0 }}>
        Self-serve, one click, no phone call. Your workspace stays live until
        the end of the current billing period, then archives for 90 days
        before data is permanently deleted.
      </p>
      <button
        type="button"
        onClick={onOpenSaveOffer}
        disabled={busy}
        style={{ ...btnGhost, marginTop: '1rem' }}
      >
        Cancel my plan
      </button>
    </section>
  );
}

// ---- Style tokens -----------------------------------------------------

const shell: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '2rem',
  display: 'grid',
  gap: '1.25rem',
  fontFamily: 'var(--font-ui, system-ui)',
};

const btnPrimary: React.CSSProperties = {
  appearance: 'none',
  padding: '0.6rem 1rem',
  borderRadius: '0.4rem',
  border: 0,
  background: 'var(--accent)',
  color: 'var(--text-inverse)',
  fontSize: '0.95rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  appearance: 'none',
  padding: '0.6rem 1rem',
  borderRadius: '0.4rem',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'inherit',
  fontSize: '0.95rem',
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  appearance: 'none',
  padding: '0.6rem 1rem',
  borderRadius: '0.4rem',
  border: 0,
  background: 'var(--critical)',
  color: 'var(--text-inverse)',
  fontSize: '0.95rem',
  fontWeight: 500,
  cursor: 'pointer',
};

// ---- Helpers ----------------------------------------------------------

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}
